import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, EventEmitter, Inject,
  inject,
  input,
  OnDestroy, OnInit,
  Output,
  PLATFORM_ID,
  signal
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ADMIN_DEFAULT_IN_PERSON_RADIUS,
  DEFAULT_IN_PERSON_RADIUS,
  FilterOptionsStore,
  InPersonRadius,
  IFilterForm,
  IFilterFormOptionValue,
  IFilterFormValues,
  ILatLon,
  PSYCHIATRIST_URL_TITLE,
} from '@sondermind/data-access/public-facing/filter-form';
import { FEATURE_FLAG_SERVICE, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { GoogleMapsGeocodeService } from '@sondermind/google-maps';
import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { IInsuranceOptionValue } from '@sondermind/utilities/models-provider-network-company';
import { BreakpointsService } from '@sondermind/utilities/services';
import { ProviderNameSearchComponent, SearchLayout } from '@sondermind/features/provider-name-search';
import { ProviderSearchHttpService } from '@sondermind/data-access/public-facing/provider-search';
import { Logger } from '@sondermind/datadog-logging';
import { IFilterCategoryConfig } from '../../models/filter-category-config.interface';
import { FilterCategory } from '../../models/filter-name.constants';
import { IOptionCategory } from '../../models/option-category.interface';
import { FilterLoadingService } from '../../services/filter-loading.service';
import { FilterParseService } from '../../services/filter-parse.service';
import { FilterValidationService } from '../../services/filter-validation.service';
import { isInsuranceInOptions } from '../../utils/insurance-selection.util';
import { FilterSelectMobileComponent } from '../filter-select-mobile/filter-select-mobile.component';
import { FilterSelectV2Component } from '../filter-select-v2/filter-select-v2.component';
import { MoreFiltersV2Component } from '../more-filters-v2/more-filters-v2.component';
import { InPersonSearchStore } from '../../stores/in-person-search.store';

// How many compact chips are shown at each viewport width. The companion sidebar is a fixed 496px,
// so viewport width maps deterministically to the space left for chips; wider viewports pull more
// filters out of "More filters". Ordered ascending by minWidth.
const COMPACT_CHIP_TIERS: ReadonlyArray<{ minWidth: number; count: number; }> = [
  { minWidth: 1280, count: 2 },
  { minWidth: 1440, count: 3 },
  { minWidth: 1600, count: 4 },
  { minWidth: 1840, count: 5 }
];

// Fewest inline chips (Insurance only), used below the smallest tier and before first measure.
const BASE_COMPACT_CHIP_COUNT = 1;

// Stable data-test hooks the desktop chips already expose (used by e2e page objects).
const CHIP_DATA_TEST: Partial<Record<FilterCategory, string>> = {
  [FilterCategory.STATE]: 'location',
  [FilterCategory.THERAPY_OR_PSYCHIATRY]: 'therapy-or-psychiatry',
  [FilterCategory.INSURANCE]: 'insurance',
  [FilterCategory.SPECIALTY]: 'concerns'
};

// `size` attribute (character width) for a chip's text input. Matches the widths the full-desktop
// chips use so the compact row lines up; Insurance holds longer plan names so it is wider.
const enum ChipSize {
  DEFAULT = 8,
  INSURANCE = 12,
}

// A compact filter with presentation fields (data-test hook, input size) precomputed at build time
// so the template reads plain properties instead of calling functions on every change detection.
interface ICompactFilterChip extends IFilterCategoryConfig {
  dataTest: string;
  size: ChipSize;
}

@Component({
  selector: 'filter-form-v2',
  standalone: true,
  imports: [
    CommonModule,
    FilterSelectV2Component,
    MoreFiltersV2Component,
    FilterSelectMobileComponent,
    ReactiveFormsModule,
    ProviderNameSearchComponent,
  ],
  providers: [InPersonSearchStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filter-form-v2.component.html',
  styleUrl: './filter-form-v2.component.scss'
})
export class FilterFormV2Component implements OnInit, OnDestroy {
  readonly internalDirectory = input<boolean>(false);

  /**
   * Marks this as Care Coordination's directory inside the admin portal (MER-5332), which gets
   * filters the public directory must not offer — currently Licensure, with Languages (MER-5333) and
   * Veteran Preferred (MER-5334) to follow.
   *
   * A component input rather than a feature flag or runtime config because the question is "which
   * surface am I?", which the consumer knows statically at the template. A flag answers "is it rolled
   * out" and would need async plumbing into `setupFilterConfigurations`, which runs synchronously
   * inside the `loadAllFilters` subscribe. Layer a flag on top of this only for rollout.
   *
   * Distinct from `internalDirectory`, which despite the name only controls whether provider-name
   * search navigates in-app to `/providers/:shareCode`.
   */
  readonly adminDirectory = input<boolean>(false);

  @Output() filterRetrievalError = new EventEmitter<boolean>();
  @Output() redirectToNotFound = new EventEmitter<boolean>();
  @Output() formFiltersChanged = new EventEmitter<Partial<IFilterFormValues>>();
  stateOptions: Array<IOptionCategory<string>> = [];
  insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [];
  specialtyOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  modalityOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  genderOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  sessionFormatOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  ageOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  treatmentPreferenceOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  serviceTypeOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  /**
   * Credential options for the admin directory (MER-5332), loaded from the provider-index `licensures`
   * facet so the list contains only credentials actually held by a live provider in the searched
   * state. Empty on every other surface, where the filter is not rendered at all.
   */
  licensureOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];

  filterFormGroup: FormGroup<IFilterForm> = new FormGroup({
    city: new FormControl<string | null>(null, Validators.required),
    state: new FormControl<string | null>(null, Validators.required),
    serviceType: new FormControl<IFilterFormOptionValue | null>(null, Validators.required),
    insurance: new FormControl<IInsuranceOptionValue | null>(null),
    specialties: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    modalities: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    genders: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    sessionFormats: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    ages: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    treatmentPreferences: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    // `licensures` is deliberately absent — it is added by addLicensureControl() only when
    // `adminDirectory` is set, so the public directory carries no control for it. See
    // IFilterForm.licensures.
    userLatLon: new FormControl<ILatLon | null>(null),
    userZip: new FormControl<string | null>(null),
    userRadius: new FormControl<InPersonRadius>(DEFAULT_IN_PERSON_RADIUS, { nonNullable: true }),
  });
  moreFiltersListDesktop: IFilterCategoryConfig[] = [];
  concernsListMobile: IFilterCategoryConfig[] = [];
  moreFiltersListMobile: IFilterCategoryConfig[] = [];

  // Single ordered list for the compact (companion open) layout. The first N entries render as
  // inline chips (N chosen by viewport tier); the remainder overflow into `more-filters-v2`.
  readonly compactDesktopFilters = signal<ICompactFilterChip[]>([]);

  readonly isCompactDesktop = input<boolean>(false);
  readonly filtersLoading = signal<boolean>(true);
  readonly isMobile = signal<boolean>(false);
  readonly isSearchPanelOpen = signal<boolean>(false);

  // How many of `compactDesktopFilters` show inline; the rest go to More filters. Always >= 1 so
  // Insurance (index 0) stays visible. Driven by media queries (see subscribeToBreakpointChanges).
  private readonly visibleCompactCount = signal<number>(BASE_COMPACT_CHIP_COUNT);

  readonly visibleCompactFilters = computed(() => this.compactDesktopFilters().slice(0, this.visibleCompactCount()));

  readonly overflowCompactFilters = computed(() => this.compactDesktopFilters().slice(this.visibleCompactCount()));

  protected readonly filterCategory = FilterCategory;
  protected readonly searchLayout = SearchLayout;
  isUpdatingForm: boolean = false;
  private destroyed$ = new Subject<void>();
  /** States to load credential options for; see subscribeToLicensureOptions. */
  private readonly licensureStateRequested = new Subject<string | null>();

  readonly inPersonSearchStore = inject(InPersonSearchStore);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    private filterLoadingService: FilterLoadingService,
    private filterValidationService: FilterValidationService,
    private filtersStore: FilterOptionsStore,
    private filterParseService: FilterParseService,
    private breakpointService: BreakpointsService,
    private geocodeService: GoogleMapsGeocodeService,
    @Inject(PLATFORM_ID) private platformId: object,
    private route: ActivatedRoute,
    @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService,
    private providerSearchHttpService: ProviderSearchHttpService,
    private logger: Logger,
  ) { }

  ngOnInit(): void {
    this.filtersLoading.set(true);

    // Initialize the in-person search store with form controls
    this.inPersonSearchStore.initializeControls({
      sessionFormats: this.filterFormGroup.controls.sessionFormats,
      userZip: this.filterFormGroup.controls.userZip,
      userLatLon: this.filterFormGroup.controls.userLatLon,
      userRadius: this.filterFormGroup.controls.userRadius,
    });
    // Zip + radius in-person search is on unconditionally for coordinators and stays behind the
    // public gate everywhere else (MER-5331).
    this.inPersonSearchStore.setAdminDirectory(this.adminDirectory());
    this.applyAdminRadiusDefault();

    this.subscribeToFormChanges();
    this.subscribeToLicensureOptions();
    this.subscribeToFilterLoadingError();
    this.subscribeToFilterValidation();
    this.subscribeToBreakpointChanges();
    this.subscribeToZipChanges();
    this.loadAllFilters();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadAllFilters(): void {
    this.filterLoadingService.getAllFilterOptions$().pipe(
      take(1),
      filter((filterOptions) => !!filterOptions),
      tap((filterOptions) => {
        this.stateOptions = filterOptions.stateOptions;
        this.insuranceOptions = filterOptions.insuranceOptions;
        this.specialtyOptions = filterOptions.specialtyOptions;
        this.modalityOptions = filterOptions.modalityOptions;
        this.genderOptions = filterOptions.genderOptions;
        this.sessionFormatOptions = filterOptions.sessionFormatOptions;
        this.ageOptions = filterOptions.ageOptions;
        this.treatmentPreferenceOptions = filterOptions.treatmentPreferenceOptions;
        this.serviceTypeOptions = filterOptions.serviceTypeOptions;
      }),
      switchMap(() => this.populateFormFromUrl$())
    ).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.filtersLoading.set(false);
      }
      this.setupFilterConfigurations();
      this.filtersStore.setIsLoaded(true);
      this.filterFormGroup.updateValueAndValidity();
      // Initial credential options. Requested rather than awaited, so the facet round-trip never
      // gates this load. No-op unless adminDirectory is set.
      this.licensureStateRequested.next(this.filterFormGroup.controls.state.value);
    });
  }

  private populateFormFromUrl$(): Observable<string | null> {
    // Prevent subscribeToFormChanges from resetting city during initial URL parsing
    this.isUpdatingForm = true;

    return this.filterParseService.parseAndPopulateV2Form(
      this.route,
      this.filterFormGroup,
      {
        stateOptions: this.stateOptions,
        insuranceOptions: this.insuranceOptions,
        specialtyOptions: this.specialtyOptions,
        modalityOptions: this.modalityOptions,
        genderOptions: this.genderOptions,
        sessionFormatOptions: this.sessionFormatOptions,
        ageOptions: this.ageOptions,
        treatmentPreferenceOptions: this.treatmentPreferenceOptions,
        serviceTypeOptions: this.serviceTypeOptions
      }
    ).pipe(
      tap(() => {
        this.isUpdatingForm = false;
      }),
      map(() => this.filterFormGroup.controls.state.value)
    );
  }

  private setupFilterConfigurations(): void {
    this.addLicensureControl();
    this.setupDesktopFilters();
    this.setupCompactDesktopFilters();
    this.setupMobileFilters();
    this.appendAdminOnlyFilters();
  }

  /**
   * Starts the admin directory's in-person search at the widest radius.
   *
   * Applied here rather than as the control's initial value because that value is shared with the
   * public directory, whose default is a product decision of its own. `emitEvent: false` keeps this
   * setup out of the form's change stream — it is an initial value, not a filter the user changed.
   */
  private applyAdminRadiusDefault(): void {
    if (!this.adminDirectory()) return;

    this.filterFormGroup.controls.userRadius.setValue(
      ADMIN_DEFAULT_IN_PERSON_RADIUS, { emitEvent: false }
    );
  }

  /**
   * Adds the `licensures` control only on the admin directory, so the public directory's form has no
   * control for a filter it must not offer and no consumer of `IFilterForm` is conscripted into
   * declaring one.
   */
  private addLicensureControl(): void {
    if (!this.adminDirectory() || this.filterFormGroup.contains('licensures')) return;

    this.filterFormGroup.addControl(
      'licensures',
      new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true })
    );
  }

  /**
   * Appends the admin-only filters to every "More filters" list.
   *
   * Appending to the three assembled lists rather than adding a `visible` flag to
   * IFilterCategoryConfig keeps all three renderers untouched — a category the coordinator should not
   * see is simply not in the list. Placement in the overflow area is deliberate: these are
   * lower-frequency filters and the primary row is already full.
   *
   * The options start empty and are filled by the facet load, which republishes these configs rather
   * than writing through the reference held here — see publishLicensureOptions.
   */
  private appendAdminOnlyFilters(): void {
    const licensureControl = this.filterFormGroup.controls.licensures;
    if (!this.adminDirectory() || !licensureControl) return;

    const licensureConfig: IFilterCategoryConfig = {
      category: FilterCategory.LICENSURE,
      options: this.licensureOptions,
      formControl: licensureControl,
      isSearchable: true,
      isMulti: true
    };

    this.moreFiltersListDesktop = [...this.moreFiltersListDesktop, licensureConfig];
    this.moreFiltersListMobile = [...this.moreFiltersListMobile, licensureConfig];
    // The compact row holds chips, and only the first `visibleCompactCount` render inline — the rest
    // overflow into More filters. Appending last therefore lands the credential filter in the
    // overflow, which is where it is wanted, without a separate visibility rule.
    this.compactDesktopFilters.update((chips) => [...chips, this.toCompactChip(licensureConfig)]);
  }

  /**
   * Credential options follow the form's own `state` control, on a dedicated subscription.
   *
   * Deliberately NOT hung off the state branch of `subscribeToFormChanges`. That branch fires only
   * when the new state differs from `FilterOptionsStore`'s current state, which is seeded from
   * localStorage — so a coordinator whose stored state already matches the referral's, the common
   * case for anyone who has used a directory before, would set the state, skip the branch, and get a
   * permanently empty credential list. Watching the control itself has no such gap.
   *
   * On its own subscription, never chained into what `formFiltersChanged` waits on: the facet list is
   * a filter's contents, not part of the search, and a facet failure is already non-fatal. Chaining it
   * would make every state change hold the provider results for an extra round-trip.
   *
   * `distinctUntilChanged` collapses the duplicate triggers (the post-load request and the control's
   * own emission for the same state) into one call; `switchMap` makes a rapid state change abandon
   * the in-flight facet request rather than let it land last and overwrite the newer list.
   */
  private subscribeToLicensureOptions(): void {
    this.licensureStateRequested.pipe(
      distinctUntilChanged(),
      switchMap((state) => this.updateLicensureOptions$(state)),
      takeUntil(this.destroyed$)
    ).subscribe();

    this.filterFormGroup.controls.state.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((state) => this.licensureStateRequested.next(state));
  }

  /**
   * Loads credential options from provider-index's `licensures` facet for the given state.
   *
   * The facet only returns credentials held by at least one live provider in that state, so the list
   * cannot offer something no search could match, and the bucket value is the abbreviation — the exact
   * value `filters.licensures` is matched on. Nothing is fetched off the admin directory.
   *
   * The loaded options are published through `publishLicensureOptions`, which rebuilds the category
   * configs — see the note there on why filling the array in place does not render.
   */
  private updateLicensureOptions$(state: string | null): Observable<unknown> {
    if (!this.adminDirectory() || !state) return of(null);

    return this.providerSearchHttpService.providerIndexFacets$({
      state,
      facetField: 'licensures'
    }).pipe(
      tap((response) => {
        const options = (response?.buckets ?? []).map((bucket) => {
          // `attributes` is an index-signature map, so bracket access is required — several consumer
          // libs (adaptive-intake, provider-profile-page, pagination and others) set
          // `noPropertyAccessFromIndexSignature`, and dot access fails to compile there even though
          // this lib's own tsconfig permits it.
          //
          // The source name is state-prefixed ("CO - Licensed Clinical Social Worker"), so strip the
          // prefix for the label and fall back to the abbreviation.
          const label = bucket.attributes?.['name']?.replace(/^[A-Za-z]{2}\s*-\s*/, '') || bucket.value;
          return {
            value: { title: label, tag: bucket.value },
            name: `${label} (${bucket.value})`
          };
        });
        this.publishLicensureOptions([{ name: FilterCategory.LICENSURE, options }]);
      }),
      catchError((error: unknown) => {
        // An empty credential list is the safe degradation: the coordinator loses one filter rather
        // than the whole directory. Logged because a silently empty dropdown is indistinguishable
        // from "this state has no credentials", which would be wrong.
        this.logger.warn('Could not load licensure filter options', {
          state,
          status: (error as { status?: number })?.status
        });
        this.publishLicensureOptions([]);
        return of(null);
      })
    );
  }

  /**
   * Publishes a loaded credential list into every rendered licensure config.
   *
   * Each config gets a NEW options array and each list a new array reference, because the renderers
   * cannot see anything less. `more-filters-v2` is `OnPush` and derives its rendered list from a
   * `computed()` over its `filterCategories` input signal and its search queries, so filling the
   * shared `licensureOptions` array in place changes neither the array identity nor any signal: the
   * computed never recomputes and the Licensure category renders empty despite a successful facet
   * response. This mirrors what the insurance path does on a state change.
   *
   * `markForCheck` because this runs from an HTTP callback, which does not by itself mark an `OnPush`
   * component dirty — without it the new input values would sit unread until some unrelated event
   * happened to trigger a check.
   */
  private publishLicensureOptions(options: Array<IOptionCategory<IFilterFormOptionValue>>): void {
    this.licensureOptions = options;

    this.moreFiltersListDesktop = this.withLicensureOptions(this.moreFiltersListDesktop);
    this.moreFiltersListMobile = this.withLicensureOptions(this.moreFiltersListMobile);
    this.compactDesktopFilters.update((chips) => chips.map((chip) => (
      chip.category === FilterCategory.LICENSURE ? { ...chip, options: this.licensureOptions } : chip
    )));

    this.changeDetectorRef.markForCheck();
  }

  private withLicensureOptions(configs: IFilterCategoryConfig[]): IFilterCategoryConfig[] {
    return configs.map((config) => (
      config.category === FilterCategory.LICENSURE ? { ...config, options: this.licensureOptions } : config
    ));
  }

  private setupDesktopFilters(): void {
    this.moreFiltersListDesktop = [
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      }
    ];
  }

  private setupCompactDesktopFilters(): void {
    // Ordered by display priority: Insurance is always visible (index 0); the rest fill the row
    // left-to-right until it runs out of width, then overflow into "More filters".
    const orderedFilters: IFilterCategoryConfig[] = [
      {
        category: FilterCategory.INSURANCE,
        options: this.insuranceOptions,
        formControl: this.filterFormGroup.controls.insurance,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.SPECIALTY,
        options: this.specialtyOptions,
        formControl: this.filterFormGroup.controls.specialties,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.STATE,
        options: this.stateOptions,
        formControl: this.filterFormGroup.controls.state,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.THERAPY_OR_PSYCHIATRY,
        options: this.serviceTypeOptions,
        formControl: this.filterFormGroup.controls.serviceType,
        isSearchable: false,
        isMulti: false
      },
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      }
    ];

    this.compactDesktopFilters.set(orderedFilters.map((config) => this.toCompactChip(config)));
  }

  // Attach presentation fields once so the template never calls functions per chip per CD cycle.
  private toCompactChip(config: IFilterCategoryConfig): ICompactFilterChip {
    const dataTest = CHIP_DATA_TEST[config.category]
      ?? `filter-${config.category.toLowerCase().replace(/\s+/g, '-')}`;
    const size = config.category === FilterCategory.INSURANCE ? ChipSize.INSURANCE : ChipSize.DEFAULT;
    return { ...config, dataTest, size };
  }

  private setupMobileFilters(): void {
    this.concernsListMobile = [
      {
        category: FilterCategory.SPECIALTY,
        options: this.specialtyOptions,
        formControl: this.filterFormGroup.controls.specialties,
        isSearchable: true,
        isMulti: true
      }
    ];

    this.moreFiltersListMobile = [
      {
        category: FilterCategory.THERAPY_OR_PSYCHIATRY,
        options: this.serviceTypeOptions,
        formControl: this.filterFormGroup.controls.serviceType,
        isSearchable: false,
        isMulti: false
      },
      {
        category: FilterCategory.INSURANCE,
        options: this.insuranceOptions,
        formControl: this.filterFormGroup.controls.insurance,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.STATE,
        options: this.stateOptions,
        formControl: this.filterFormGroup.controls.state,
        isSearchable: true,
        isMulti: false
      }
    ];
  }

  updateFormValues(formValues: Partial<IFilterFormValues>): void {
    this.filterFormGroup.patchValue(formValues);
  }

  private updateMobileInsuranceFilter(): void {
    const insuranceFilterIndex = this.moreFiltersListMobile.findIndex(
      (filterConfig) => filterConfig.category === FilterCategory.INSURANCE
    );
    if (insuranceFilterIndex !== -1) {
      this.moreFiltersListMobile[insuranceFilterIndex] = {
        ...this.moreFiltersListMobile[insuranceFilterIndex],
        options: this.insuranceOptions
      };
    }

    this.updateCompactInsuranceFilter();
  }

  private updateCompactInsuranceFilter(): void {
    this.compactDesktopFilters.update((filters) => filters.map((filterConfig) => {
      if (filterConfig.category !== FilterCategory.INSURANCE) return filterConfig;
      return { ...filterConfig, options: this.insuranceOptions };
    }));
  }

  private updateInsuranceOptions$() {
    return this.filterLoadingService.getInsuranceOptions$().pipe(
      map((insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>>) => {
        this.insuranceOptions = insuranceOptions;
        this.updateMobileInsuranceFilter();
        return this.filterFormGroup.value;
      })
    );
  }

  private subscribeToFilterLoadingError(): void {
    // Handle errors from loader
    this.filterLoadingService.filterError$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((hasError) => {
      if (hasError) {
        this.filterRetrievalError.emit(true);
      }
    });
  }

  private subscribeToFilterValidation(): void {
    this.filterValidationService.setupFilterValidation$(this.route).pipe(
      takeUntil(this.destroyed$)
    ).subscribe();

    this.filterValidationService.invalidFilterState$.pipe(
      takeUntil(this.destroyed$),
      filter((x) => x),
      // Validators run synchronously in one tick, so several can flip the state to true
      // in the same navigation. take(1) collapses those into a single redirect.
      take(1)
    ).subscribe(() => {
      this.redirectToNotFound.emit(true);
    });
  }

  private subscribeToBreakpointChanges(): void {
    this.breakpointService.isMobile$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isMobile) => {
      this.isMobile.set(isMobile);
    });

    this.subscribeToCompactChipTierChanges();
  }

  private subscribeToCompactChipTierChanges(): void {
    const tierQueries = COMPACT_CHIP_TIERS.map((tier) => `(min-width: ${tier.minWidth}px)`);

    this.breakpointObserver.observe(tierQueries).pipe(
      map((state) => this.widestMatchedTierCount(state.breakpoints)),
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe((count) => {
      this.visibleCompactCount.set(count);
    });
  }

  // Highest tier whose min-width query currently matches; base count (Insurance only) if none do.
  private widestMatchedTierCount(matches: Record<string, boolean>): number {
    let count = BASE_COMPACT_CHIP_COUNT;
    COMPACT_CHIP_TIERS.forEach((tier) => {
      if (matches[`(min-width: ${tier.minWidth}px)`]) {
        count = tier.count;
      }
    });
    return count;
  }

  updateFormWithPsychiatricOption(): void {
    // Keep current state and city values
    const currentState = this.filterFormGroup.controls.state.value;
    const currentCity = this.filterFormGroup.controls.city.value;

    // Find the psychiatry option from the modality options
    const psychiatryOption = this.modalityOptions[0]?.options?.find(
      (option) => option.value.title === PSYCHIATRIST_URL_TITLE
    )?.value;

    if (!psychiatryOption) {
      return; // Psychiatry option not found
    }

    // Create form values with psychiatry option
    const psychiatricFormValues: Partial<IFilterFormValues> = {
      state: currentState,
      city: currentCity,
      insurance: null,
      specialties: [],
      modalities: [psychiatryOption],
      genders: [],
      sessionFormats: [],
      ages: [],
      treatmentPreferences: []
    };

    this.updateFormValues(psychiatricFormValues);
  }

  private subscribeToZipChanges(): void {
    this.filterFormGroup.controls.userZip.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.inPersonSearchStore.zipError.set(null);
        this.inPersonSearchStore.zipLoading.set(false);
      }),
      switchMap((zip) => {
        if (!zip || !/^\d{5}$/.test(zip)) {
          this.filterFormGroup.controls.userLatLon.setValue(null, { emitEvent: false });
          return EMPTY;
        }
        this.inPersonSearchStore.zipLoading.set(true);
        return this.geocodeService.forwardGeocode(zip).pipe(
          catchError(() => {
            this.inPersonSearchStore.zipError.set('Could not reach location service. Please try again.');
            return of(null);
          })
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe((latLon: ILatLon | null) => {
      this.inPersonSearchStore.zipLoading.set(false);
      this.filterFormGroup.controls.userLatLon.setValue(latLon, { emitEvent: false });

      // Show "not found" on a null result, but do not overwrite a distinct error already set by
      // the geocode catchError (e.g. a service outage) — a real outage must not be mislabeled as
      // an invalid ZIP. MER-5125.
      if (latLon === null && !this.inPersonSearchStore.zipError()) {
        this.inPersonSearchStore.zipError.set('Zip code not found. Try another.');
      }

      // Always emit so downstream results reconcile. On a not-found/failed geocode this emits
      // the cleared userLatLon, replacing stale results + the 'N providers near <zip>' heading
      // rather than leaving the prior search rendered alongside the error. MER-5125.
      this.formFiltersChanged.emit(this.filterFormGroup.value);
    });
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.valueChanges.pipe(
      filter(() => !this.isUpdatingForm),
      switchMap((values) => {
        if (!!values?.state && values.state !== this.filtersStore.getCurrentState()) {
          this.isUpdatingForm = true;
          this.filterFormGroup.patchValue({ city: EmptyURIValue, insurance: null });
          this.filtersStore.setCurrentState(values.state);
          this.isUpdatingForm = false;
          // Credentials are also issued per state, but they are loaded from a subscription on the
          // state control itself (subscribeToLicensureOptions) rather than from here — this branch
          // is skipped whenever the store already holds the new state, and it gates the emit below.
          return this.updateInsuranceOptions$();
        }
        // Update insurance options when switching between therapy and psychiatry
        const isPsychiatrist = values?.serviceType?.title === PSYCHIATRIST_URL_TITLE;
        const didIsPsychiatristChange = isPsychiatrist !== this.filtersStore.getCurrentIsPsychiatrist();
        if (didIsPsychiatristChange) {
          this.isUpdatingForm = true;
          this.filtersStore.setCurrentIsPsychiatrist(isPsychiatrist);
          return this.updateInsuranceOptions$().pipe(
            map(() => {
              const selectedInsurance = this.filterFormGroup.value.insurance;
              if (!isInsuranceInOptions(selectedInsurance, this.insuranceOptions)) {
                this.filterFormGroup.patchValue({ insurance: null });
              }
              this.isUpdatingForm = false;
              return this.filterFormGroup.value;
            })
          );
        }

        return of(values);
      }),
      takeUntil(this.destroyed$)
    ).subscribe((values) => {
      this.formFiltersChanged.emit(values);
    });
  }
}
